// =============================================================================
// ProtectedRoute — requires Supabase session + partner membership
// =============================================================================

import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuthStore } from "@veorest/api-client";
import { ROUTES } from "../routes";
import { useI18n } from "../i18n";
import {
  mustChangePassword,
  resolvePartnerSession,
  usePartnerSessionStore,
} from "../lib/partnerAuth";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, signOut } = useAuthStore();
  const { t } = useI18n();
  const location = useLocation();
  const { session, setSession, setResolving, clear } = usePartnerSessionStore();
  const [gate, setGate] = useState<"pending" | "ok" | "password" | "denied">(
    "pending"
  );
  const [deniedReason, setDeniedReason] = useState<"none" | "membership">(
    "none"
  );

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      clear();
      setGate("denied");
      return;
    }

    if (mustChangePassword(user)) {
      setGate("password");
      return;
    }

    let cancelled = false;
    setResolving(true);

    resolvePartnerSession()
      .then(async (partnerSession) => {
        if (cancelled) return;

        if (!partnerSession) {
          clear();
          await signOut();
          setDeniedReason("membership");
          setGate("denied");
          return;
        }

        setSession(partnerSession);
        setGate("ok");
      })
      .catch(async () => {
        if (cancelled) return;
        clear();
        await signOut();
        setDeniedReason("membership");
        setGate("denied");
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, isLoading, signOut, setSession, setResolving, clear]);

  if (isLoading || gate === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface)]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Loading partner workspace…</p>
        </div>
      </div>
    );
  }

  if (gate === "password") {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }

  if (!user || gate === "denied" || !session) {
    if (gate === "denied" && deniedReason === "membership") {
      toast.error(t("auth.notPartner"), { id: "partner-membership" });
    }
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
